import React, { ReactNode } from 'react'
import {
    StyledHeader,
    StyledHeaderContainer,
    StyledHeaderIntent,
    StyledIconContainer,
    StyledImage,
    StyledMainContainer,
    StyledSubText,
    StyledSupportContainer,
    TWContentBlock,
    TWContentSection,
} from './TWContentBlockFullWidth.styled'

type TWContentBlockFullWidthProps = {
    heading: string
    subText: string
    imgSrc: string
    /** Applies bold and italics to the specified range in the heading */
    headingIntent?: {
        headingIntentStartIndex: number
        headingIntentLength: number
    }
    /** Changes background of full width section */
    backgroundColor?: string
    /** Position of content to be displayed - text on the left or right */
    contentPosition?: 'left' | 'right'
    /** Source of the icon to be displayed if any */
    headerIcon?: ReactNode
    /** Determines if the full width section should have bottom padding or not for bottom alligned images */
    isBottomAligned?: boolean
}

export const TWContentBlockFullWidth = ({
    heading,
    subText,
    imgSrc,
    headingIntent,
    backgroundColor,
    isBottomAligned,
    contentPosition,
    headerIcon,
}: TWContentBlockFullWidthProps) => {
    let styledHeading: ReactNode = <StyledHeader>{heading}</StyledHeader>

    // Apply italics and bold to heading based on the specified intent range
    if (headingIntent) {
        const { headingIntentStartIndex: start, headingIntentLength: length } =
            headingIntent

        // Splits the heading into words to determine the words to apply intent to
        const words = Array.from(heading.matchAll(/\S+/g))

        if (
            Number.isInteger(start) &&
            Number.isInteger(length) &&
            start >= 0 &&
            length > 0 &&
            start + length <= words.length
        ) {
            const startOffset = words[start].index!
            const lastWord = words[start + length - 1]
            const endOffset = lastWord.index! + lastWord[0].length

            styledHeading = (
                <StyledHeader>
                    {heading.slice(0, startOffset)}
                    <StyledHeaderIntent>
                        {heading.slice(startOffset, endOffset)}
                    </StyledHeaderIntent>
                    {heading.slice(endOffset)}
                </StyledHeader>
            )
        }
    }

    return (
        <TWContentBlock
            backgroundColor={backgroundColor}
            hasBottomPadding={!isBottomAligned}
        >
            <TWContentSection>
                <StyledMainContainer
                    order={contentPosition === 'right' ? 2 : 1}
                    hasBottomPadding={isBottomAligned}
                >
                    <StyledHeaderContainer>
                        {headerIcon && (
                            <StyledIconContainer>
                                {headerIcon}
                            </StyledIconContainer>
                        )}
                        {styledHeading}
                    </StyledHeaderContainer>
                    <StyledSubText>{subText}</StyledSubText>
                </StyledMainContainer>
                <StyledSupportContainer
                    order={contentPosition === 'right' ? 1 : 2}
                    isBottomAligned={isBottomAligned}
                >
                    <StyledImage
                        src={imgSrc}
                        isBottomAligned={isBottomAligned}
                    />
                </StyledSupportContainer>
            </TWContentSection>
        </TWContentBlock>
    )
}
